/* eslint-disable @typescript-eslint/no-explicit-any */
import bcrypt from 'bcryptjs';
import { jwtDecode } from 'jwt-decode';
import { dbGetAll } from './db';
import { ApiLogin, aplicarToken } from './plataforma';

// Login sem internet. A sincronização baixa os usuários ativos da mesma
// empresa de quem está logado (GET /usuariosoffline no servidor de login, com
// o hash bcrypt da senha — ver sincronizacao.ts). Sem conexão, a senha é
// conferida aqui contra esse hash e o sistema entra com um token LOCAL: mesmo
// formato e mesmos dados do token do servidor (iduser, idempresa, banco,
// configurações da empresa), mas sem assinatura válida — serve só pro app
// ler os dados do usuário; o servidor recusaria. Por isso, quando a conexão
// volta, renovarLoginOnline() faz o login de verdade com a mesma senha
// (guardada só em memória, nunca gravada) antes de sincronizar qualquer coisa.

const VALIDADE_TOKEN_LOCAL_S = 12 * 60 * 60;

let credenciais: { login: string; senha: string } | null = null;

// Chamado também no login online: se a internet cair e voltar sem recarregar
// a página, a renovação já tem a senha (o token online não precisa, mas o
// custo é nulo e cobre um login offline feito depois, na mesma aba).
export const lembrarCredenciais = (login: string, senha: string) => {
  credenciais = { login, senha };
};

const base64Url = (valor: unknown) => btoa(unescape(encodeURIComponent(JSON.stringify(valor))))
  .replace(/\+/g, '-')
  .replace(/\//g, '_')
  .replace(/=+$/, '');

export const autenticarOffline = async (login: string, senha: string): Promise<string | Error> => {
  const usuarios = await dbGetAll<any>('usuarios').catch(() => [] as any[]);
  if (!usuarios.length) {
    return new Error('Sem conexão com o servidor, e este computador ainda não tem os usuários para login offline. Entre uma vez com internet.');
  }
  // Mesma comparação do servidor (login exato).
  const usuario = usuarios.find((u) => u.login === login.trim());
  const senhaConfere = !!usuario?.senha && await bcrypt.compare(senha, usuario.senha);
  if (!senhaConfere) return new Error('Usuário ou senha inválidos (login offline).');

  const agora = Math.floor(Date.now() / 1000);
  const payload = {
    iduser: usuario.iduser,
    img: usuario.img,
    idempresa: usuario.idempresa,
    banco: usuario.banco,
    decimais: usuario.decimais,
    tipoimp: usuario.tipoimp,
    est: usuario.est,
    refs: usuario.refs,
    margem: usuario.margem,
    crt: usuario.crt,
    iat: agora,
    exp: agora + VALIDADE_TOKEN_LOCAL_S,
    offline: true,
  };
  lembrarCredenciais(login.trim(), senha);
  return `${base64Url({ alg: 'none', typ: 'JWT' })}.${base64Url(payload)}.offline`;
};

export const tokenAtualEhOffline = (): boolean => {
  try {
    const salvo = localStorage.getItem('APP_ACCESS_TOKEN');
    if (!salvo) return false;
    let token = salvo;
    try { token = JSON.parse(salvo); } catch { /* token salvo sem aspas */ }
    return jwtDecode<{ offline?: boolean }>(token).offline === true;
  } catch {
    return false;
  }
};

// Troca o token local por um token de verdade do servidor. true = já estava
// com token do servidor ou renovou; false = não deu (sem a senha em memória
// porque a página foi recarregada, ou senha trocada no servidor) — aí o
// usuário precisa entrar de novo com internet.
export const renovarLoginOnline = async (): Promise<boolean> => {
  if (!tokenAtualEhOffline()) return true;
  if (!credenciais) return false;
  try {
    const { data } = await ApiLogin.post('/login', credenciais);
    if (!data?.accessToken) return false;
    aplicarToken(data.accessToken);
    return true;
  } catch {
    return false;
  }
};
